import React, { useState } from 'react';
import { Button, Select, Space, Spin } from 'antd';
import Title from 'antd/es/typography/Title';
import * as libCon from '../community-hats-js-library/Constants.js';
import { exportParticipantsTable } from '../community-hats-js-library/utils/exportFunctions';
import { getCurrentSurveysURL, getLabAndSurveySummaryURL } from '../community-hats-js-library/back-end/router';
import { showError } from '../utils/generalFunctions.js';
import { RefContextLanguage } from '../providers/LanguageProvider';

// The languages the surveys are offered in here. documentsFunctions also
// publishes 'es', which this deployment has no use for.
//
// Each option is labeled in its own language rather than translated: the label
// names the language of the document being downloaded, which does not depend on
// the language the interface happens to be in.
const SURVEY_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'हिन्दी' },
  { value: 'gu', label: 'ગુજરાતી' },
];

/**
 * Hands the browser a presigned URL to download.
 *
 * A same tab anchor rather than window.open: the URL is only minted after an
 * await, which a popup blocker reads as not user initiated. S3 serves these as
 * .xlsx and .docx, so the browser downloads them and never leaves the page. No
 * download attribute -- it is ignored cross origin.
 */
const openDownloadURL = (url) => {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

function DashboardDocuments() {
  const i18n = RefContextLanguage();

  const [downloadingParticipants, setDownloadingParticipants] = useState(false);
  const [downloadingSummary, setDownloadingSummary] = useState(false);
  const [downloadingSurveys, setDownloadingSurveys] = useState(false);

  const [surveyLanguage, setSurveyLanguage] = useState(SURVEY_LANGUAGE_OPTIONS[0].value);

  // The participants table is built in the browser from Airtable records, so
  // this one returns a bare status and downloads itself.
  const downloadParticipants = async () => {
    setDownloadingParticipants(true);
    const status = await exportParticipantsTable();
    if (status !== libCon.OK) showError(`${i18n.t('couldNotDownloadParticipantsTable')} (${status})`);
    setDownloadingParticipants(false);
  };

  // The share bucket documents come back as a presigned URL instead of bytes.
  const downloadSignedDocument = async (getURL, setDownloading, documentName) => {
    setDownloading(true);
    const [status, url] = await getURL();
    if (status !== libCon.OK) showError(`${i18n.t('couldNotDownload')} ${documentName} (${status})`);
    else openDownloadURL(url);
    setDownloading(false);
  };

  return (
    <div className="App">
      <header className="App-header">
        <Space direction="vertical">
          <Title level={2}>{i18n.t('documents')}</Title>

          {downloadingParticipants ? (
            <Spin />
          ) : (
            <Button type="primary" onClick={() => downloadParticipants()}>
              {i18n.t('downloadCurrentParticipantsTable')}
            </Button>
          )}

          {downloadingSummary ? (
            <Spin />
          ) : (
            <Button
              type="primary"
              onClick={() =>
                downloadSignedDocument(getLabAndSurveySummaryURL, setDownloadingSummary, i18n.t('labAndSurveySummary'))
              }
            >
              {i18n.t('downloadLabAndSurveySummary')}
            </Button>
          )}

          <Space direction="horizontal">
            {downloadingSurveys ? (
              <Spin />
            ) : (
              <Button
                type="primary"
                onClick={() =>
                  downloadSignedDocument(
                    () => getCurrentSurveysURL(surveyLanguage),
                    setDownloadingSurveys,
                    i18n.t('currentSurveys')
                  )
                }
              >
                {i18n.t('downloadCurrentSurveys')}
              </Button>
            )}
            <Select value={surveyLanguage} onChange={setSurveyLanguage} options={SURVEY_LANGUAGE_OPTIONS} />
          </Space>
        </Space>
      </header>
    </div>
  );
}

export default DashboardDocuments;
